// useDashboardLayoutSourceData.js

import { useEffect, useMemo, useState } from 'react';

import { listChildNodesByParentKeys, subscribeToNodes } from 'services/nodes/NodeReadService';
import { subscribeToViews } from 'services/views/ViewReadService';
import {
  collectViewIdsFromBlocks,
  resolveDashboardLayoutDocument,
} from 'services/dashboards/DashboardLayoutModel';

function normalizeString(value) {
  return String(value || '').trim();
}

function getViewKey(view) {
  return normalizeString(view?.key || view?.id);
}

function pickReferencedViews(allViews, referencedViewIds) {
  if (!referencedViewIds.length) return [];

  const viewMap = new Map();
  (Array.isArray(allViews) ? allViews : []).forEach((view) => {
    const key = getViewKey(view);
    if (!key) return;
    viewMap.set(key, view);
  });

  return referencedViewIds
    .map((viewId) => viewMap.get(viewId) || null)
    .filter(Boolean);
}

/**
 * useDashboardLayoutSourceData
 *
 * Loads base views and node data needed to preview dashboard VIEW blocks.
 * Only fetches nodes for views referenced by the current dashboard layout.
 *
 * @param {object} options - Hook options.
 * @param {object|null} options.dashboard - Dashboard document.
 * @param {string} options.baseKey - Base key for subscriptions.
 * @param {array|null} [options.providedViews=null] - When set, skips view subscription and uses this list.
 * @returns {{ childNodesByParent: object, referencedViewIds: string[], views: array, baseNodes: array }}
 */
export default function useDashboardLayoutSourceData({
  dashboard = null,
  baseKey,
  providedViews = null,
} = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const usesProvidedViews = providedViews != null;

  const [subscribedViews, setSubscribedViews] = useState([]);
  const [baseNodes, setBaseNodes] = useState([]);
  const [childNodesByParent, setChildNodesByParent] = useState({});

  const referencedViewIds = useMemo(() => {
    const resolved = resolveDashboardLayoutDocument(dashboard);
    return collectViewIdsFromBlocks(resolved.blocksByKey);
  }, [dashboard]);

  const views = useMemo(() => (
    usesProvidedViews
      ? (Array.isArray(providedViews) ? providedViews : [])
      : subscribedViews
  ), [providedViews, subscribedViews, usesProvidedViews]);

  const referencedViews = useMemo(() => (
    pickReferencedViews(views, referencedViewIds)
  ), [referencedViewIds, views]);

  useEffect(() => {
    if (usesProvidedViews) return undefined;

    if (!cleanedBaseKey) {
      setSubscribedViews([]);
      return undefined;
    }

    const unsubscribe = subscribeToViews(
      { baseKey: cleanedBaseKey },
      (rows) => {
        setSubscribedViews(Array.isArray(rows) ? rows : []);
      },
    );

    return () => unsubscribe?.();
  }, [cleanedBaseKey, usesProvidedViews]);

  useEffect(() => {
    if (!cleanedBaseKey) {
      setBaseNodes([]);
      return undefined;
    }

    const baseRootViews = referencedViews.filter((view) => !normalizeString(view?.nodeKey));

    if (!baseRootViews.length) {
      setBaseNodes([]);
      return undefined;
    }

    const typeKeys = Array.from(new Set(
      baseRootViews
        .map((view) => normalizeString(view?.nodeType || view?.typeKey) || 'NODE')
        .filter(Boolean),
    ));

    const params = [
      { field: 'baseKey', operator: '==', value: cleanedBaseKey },
    ];

    if (typeKeys.length === 1) {
      params.push({ field: 'type', operator: '==', value: typeKeys[0] });
    } else if (typeKeys.length > 1 && typeKeys.length <= 10) {
      params.push({ field: 'type', operator: 'in', value: typeKeys });
    }

    const unsubscribe = subscribeToNodes(
      { params },
      (rows) => {
        setBaseNodes(Array.isArray(rows) ? rows : []);
      },
    );

    return () => unsubscribe?.();
  }, [cleanedBaseKey, referencedViews]);

  useEffect(() => {
    const parentKeys = Array.from(new Set(
      referencedViews
        .map((view) => normalizeString(view?.nodeKey))
        .filter(Boolean),
    ));

    if (!parentKeys.length) {
      setChildNodesByParent({});
      return undefined;
    }

    let isCancelled = false;

    listChildNodesByParentKeys(parentKeys, {
      includeSecured: false,
      baseKey: cleanedBaseKey || null,
    })
      .then((next) => {
        if (isCancelled) return;
        setChildNodesByParent(next || {});
      })
      .catch((error) => {
        console.warn('[useDashboardLayoutSourceData] child node load failed:', error);
        if (isCancelled) return;

        const fallback = {};
        parentKeys.forEach((nodeKey) => {
          fallback[nodeKey] = [];
        });
        setChildNodesByParent(fallback);
      });

    return () => {
      isCancelled = true;
    };
  }, [cleanedBaseKey, referencedViews]);

  return {
    childNodesByParent,
    referencedViewIds,
    views,
    baseNodes,
  };
}
